/**
 * LoadingPlaceholder component for the Indigo design system.
 *
 * A versatile loading spinner component that can be used to indicate loading states
 * across the application. Supports multiple visual styles and sizes.
 *
 * Features:
 * - Light and dark variants
 * - Customizable size
 * - Smooth animation using CSS keyframes
 * - Accessible with screen reader support
 * - Uses design system colors and tokens
 *
 * @example
 * ```tsx
 * // Default light variant
 * <LoadingPlaceholder />
 *
 * // no logo variant
 * <LoadingPlaceholder noLogo />
 *
 * // Default light variant with standard sizes (sm, md, lg)
 * <LoadingPlaceholder size="sm" />
 *
 * // Dark variant with custom size
 * <LoadingPlaceholder variant="dark" width={48} height={48} />
 * ```
 */
import { cva, type VariantProps } from "class-variance-authority";
import "./LoadingPlaceholder.css";

const loadingVariants = cva("indigo-loading-placeholder", {
  variants: {
    variant: {
      light: "light",
      dark: "dark",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface LoadingPlaceholderProps
  extends VariantProps<typeof loadingVariants> {
  /**
   * Width of the loading spinner in pixels. This will override the width set by the `size` prop.
   * @default 32
   */
  width?: number;

  /**
   * Height of the loading spinner in pixels. This will override the height set by the `size` prop.
   * @default 32
   */
  height?: number;

  /**
   * The size of the loading spinner.
   * Can be 'sm' (24x24), 'md' (32x32), or 'lg' (48x48).
   * @default 'md'
   */
  size?: "sm" | "md" | "lg";

  /**
   * Progress value between 0 and 100
   * When provided, displays progress circle instead of animated spinner
   */
  progress?: number;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Hide logo parts
   * @default false
   */
  noLogo?: boolean;
}

/**
 * LoadingPlaceholder component
 *
 * @param props - Component props (@see LoadingPlaceholderProps)
 * @returns A React component for displaying a loading spinner
 */
export const LoadingPlaceholder = ({
  width: widthOverride,
  height: heightOverride,
  size = "md",
  variant,
  progress,
  className,
  noLogo = false,
}: LoadingPlaceholderProps) => {
  const dimensions = {
    sm: { width: 24, height: 24 },
    md: { width: 32, height: 32 },
    lg: { width: 48, height: 48 },
  };

  const { width: defaultWidth, height: defaultHeight } = dimensions[size];

  const width = widthOverride ?? defaultWidth;
  const height = heightOverride ?? defaultHeight;

  // Calculate progress circle values
  const radius = 17;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset =
    progress !== undefined
      ? circumference - (progress / 100) * circumference
      : 0;

  const hasProgress = progress !== undefined;

  return (
    <div
      className={loadingVariants({
        variant,
        className: `${hasProgress ? "has-progress" : ""} ${className || ""}`,
      })}
      role="status"
    >
      <div className="indigo-loading-container">
        <svg
          width={width}
          height={height}
          viewBox="0 0 36 36"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Background circle */}
          <circle
            className="indigo-loading-bg"
            cx="18"
            cy="18"
            r={radius}
            fill="none"
            strokeWidth="1.5"
          />

          {/* Progress circle (only shown when progress is provided) */}
          {hasProgress && (
            <circle
              className="indigo-loading-progress"
              cx="18"
              cy="18"
              r={radius}
              fill="none"
              strokeWidth="1.5"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 18 18)"
            />
          )}

          {/* Logo parts */}
          {!noLogo && (
            <>
              <path
                className="indigo-loading-part indigo-loading-part1"
                d="M15.7471 25.6208C18.2356 25.6208 20.4193 21.812 20.4193 17.9777C20.4193 15.6671 19.5306 14.7529 18.3117 14.7529C15.8233 14.7529 13.6142 18.5618 13.6142 22.396C13.6142 24.7067 14.5029 25.6208 15.7471 25.6208Z"
              />
              <path
                className="indigo-loading-part indigo-loading-part2"
                d="M9.82226 25.6215C11.7013 25.6215 13.0217 23.4378 13.0217 21.3048C13.0217 19.8575 12.4123 19.0195 11.2442 19.0195C9.33981 19.0195 7.99402 21.2541 7.99402 23.387C7.99402 24.7582 8.62882 25.6215 9.82226 25.6215Z"
              />
              <path
                className="indigo-loading-part indigo-loading-part3"
                d="M20.3429 22.38C19.7727 24.2707 20.493 25.6213 21.9335 25.6213H27.6817V10.0537H24.0349L23.0139 13.5265L20.3429 22.38Z"
              />
            </>
          )}
        </svg>
      </div>
      <span className="sr-only">
        {hasProgress ? `Loading ${Math.round(progress)}%` : "Loading..."}
      </span>
    </div>
  );
};

export { loadingVariants };
export default LoadingPlaceholder;
